// Everything the report, the DXF and the position card read besides the CalcState: in the reference tool these were
// read straight from the DOM (selected material labels, spacing fields, loads, service class, project data...).
import type { EngineTexts } from "../../../http/screwConnector/formulas/ctx";
import type { ConnectionType } from "../../../http/screwConnector/formulas/types";

export type ReportMeta = {
  projectName: string;
  calculationName: string;
  author?: string | null;
  // calculation date (Date, ISO string or "YYYY-MM-DD"); today when missing
  date?: Date | string | null;
};

// Material selection of an element, as the "Tipo di materiale / Specie legnosa / Classe" selects show it.
export type ReportMaterial = {
  tipo: string;
  specie: string;
  classe: string;
  customName: string | null; // name of a USER material
};

// Effective minimum distances of an element (fields a1e{n}, a2e{n}, a3t{n}, a3c{n}, a4t{n}, a4c{n}; NaN when empty).
export type ReportSpacing = { a1: number; a2: number; a3t: number; a3c: number; a4t: number; a4c: number };

export type ReportContext = {
  connectionType: ConnectionType;
  texts: EngineTexts;
  family: string; // selected screw family (fallback when the state has no screw record)
  rows: number;
  cols: number;
  serviceClass: string;
  materials: { 1: ReportMaterial; 2: ReportMaterial };
  spacing: { 1: ReportSpacing; 2: ReportSpacing };
  meta: ReportMeta;
};

export type ReportContextInput = {
  connectionType?: string | null;
  element1CustomMaterialName?: string | null;
  element2CustomMaterialName?: string | null;
};

// Builds the context from the persisted input and the engine's effective control values (EngineOutput.values, keyed
// by the reference tool's control ids).
export function buildReportContext(
  input: ReportContextInput,
  engineOutput: { values: object } | null,
  meta: ReportMeta,
  texts: EngineTexts,
): ReportContext {
  const values = (engineOutput?.values ?? {}) as Record<string, unknown>;
  const str = (k: string): string => {
    const v = values[k];
    return v == null ? "" : String(v);
  };
  const num = (k: string): number => {
    const v = values[k];
    if (typeof v === "number") return v;
    return v == null || v === "" ? NaN : parseFloat(String(v));
  };
  const material = (n: 1 | 2): ReportMaterial => ({
    tipo: str(`mat${n}Tipo`),
    specie: str(`mat${n}Specie`),
    classe: str(`mat${n}Classe`),
    customName:
      str(`mat${n}CustomName`) || (n === 1 ? input.element1CustomMaterialName : input.element2CustomMaterialName) || null,
  });
  const spacing = (n: 1 | 2): ReportSpacing => ({
    a1: num(`a1e${n}`),
    a2: num(`a2e${n}`),
    a3t: num(`a3t${n}`),
    a3c: num(`a3c${n}`),
    a4t: num(`a4t${n}`),
    a4c: num(`a4c${n}`),
  });
  return {
    connectionType: (str("materiale1") || input.connectionType || "legno") as ConnectionType,
    texts,
    family: str("etaFamiglia"),
    rows: Math.trunc(num("rows")) || 0,
    cols: Math.trunc(num("cols")) || 0,
    serviceClass: str("classeServizio"),
    materials: { 1: material(1), 2: material(2) },
    spacing: { 1: spacing(1), 2: spacing(2) },
    meta,
  };
}

export function reportDate(meta: ReportMeta): Date {
  const d = meta.date;
  if (d instanceof Date) return d;
  if (typeof d === "string" && d) {
    const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

export function formatReportDate(meta: ReportMeta): string {
  return reportDate(meta).toLocaleDateString("it-IT");
}
